import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Checkbox, Divider, Modal, message, Radio, Space, Typography } from 'antd';
import { isAxiosError } from 'axios';

import { listCatalogueTemplates } from '@eshop/templates';
import {
  DEFAULT_CATALOGUE_DESIGN,
  useCatalogueDesignOptions,
  useOptionalCatalogueAppearance,
  type CatalogueDesignKey,
} from '@eshop/designs';
import type { TemplateSlotEntry } from './schema';
import { resetTemplateDefinitionCache } from './schema';
import { useAppDispatch, useAppSelector } from '@store/store.tsx';
import { setAspectRadio } from '@store/slices/eshopSlice.tsx';
import { mergeSiteSettingsLocal, selectSiteSettings } from '@store/slices/settingsSlice.tsx';
import { useTemplateDraft, type TemplateSectionKey } from './hooks/useTemplateDraft';
import {
  fetchCatalogueTemplateSnapshot,
  saveCatalogueTemplate,
  type CatalogueTemplateSnapshot,
} from './api/catalogueTemplateClient';
// import SlotListEditor from './components/SlotListEditor';
import BlockSettingsModal from './components/BlockSettingsModal';
import LayoutEditorModal from './components/LayoutEditorModal';
import type { LayoutEditorTabKey } from './components/LayoutEditorTabs';
import { type ButtonPanelConfigEstiloRef } from '@components/settings/RenderEstiloConfig.tsx';
import {
  TypographyPanel,
  type TypographyPanelRef,
} from '@features/theme/components/TypographyPanel.tsx';
import '@styles/catalogue/layout-editor.css';
import { logInfo } from '@tools/LogsHandler.tsx';

const formatTimestamp = (iso?: string | null) => {
  if (!iso) {
    return null;
  }
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleString();
  } catch (error) {
    return null;
  }
};

// const combineBlocks = (blocks: { header: string[]; panel: string[]; card: string[] }) =>
//   Array.from(new Set([...blocks.header, ...blocks.panel, ...blocks.card]));

// const getBlockOptionsForSlot = (
//   slotKey: string,
//   blocks: { header: string[]; panel: string[]; card: string[] },
// ): string[] => {
//   switch (slotKey) {
//     case 'header':
//       return blocks.header;
//     case 'panel':
//       return blocks.panel;
//     case 'card':
//       return blocks.card;
//     default:
//       return combineBlocks(blocks);
//   }
// };
//
// const slotDescriptions: Record<string, string> = {
//   header: 'Bloques visibles en el encabezado (Brand, Navigation, Actions, etc.).',
//   panel: 'Sección principal del catálogo (Hero, Toolbar, Grid, EmptyState, Pagination).',
//   card: 'Composición de la tarjeta de producto (Media, Title, Price, Highlights, CartActions).',
// };

/** Extended section keys including UI-only sections not tied to template regions */
type ExtendedSectionKey = TemplateSectionKey | 'storeHero' | 'topFilters';

const sectionMetadata: Record<ExtendedSectionKey, { label: string; description?: string }> = {
  storeHero: {
    label: 'Encabezado de Tienda',
    description: 'Muestra el logo, nombre y descripción de la tienda en la parte superior del catálogo.',
  },
  hero: {
    label: 'Títulos y Banners',
    description: 'Sección de títulos y banners destacados en el catálogo (debajo del encabezado de tienda).',
  },
  header: {
    label: 'Header',
    description: 'Incluye logo, navegación y acciones superiores del catálogo.',
  },
  panel: {
    label: 'Panel',
    description: 'Muestra el listado principal de productos, filtros y paginado.',
  },
  topFilters: {
    label: 'Filtros superiores',
    description: 'Barra de filtros y orden superior del catálogo.',
  },
  sidebar: {
    label: 'Sidebar',
    description: 'Barra lateral con filtros avanzados o contenido complementario.',
  },
  footer: {
    label: 'Footer',
    description: 'Sección inferior con acciones o información adicional.',
  },
};

const TemplateConfigurator: React.FC = () => {
  const [modal, modalContextHolder] = Modal.useModal();
  const [params, setParams] = useSearchParams();
  const templateParam = params.get('template') || 'classic';

  const templateOptions = useMemo(
    () =>
      listCatalogueTemplates().map((name) => ({
        label: name,
        value: name,
      })),
    [],
  );

  const dispatch = useAppDispatch();
  const globalItemAspectRatio = useAppSelector((state) => state.eshop.cfg.item_aspect_card);

  const designOptions = useCatalogueDesignOptions();
  const appearance = useOptionalCatalogueAppearance();
  const firstDesign = designOptions[0]?.key ?? DEFAULT_CATALOGUE_DESIGN;
  const siteSettings = useAppSelector(selectSiteSettings);

  const [activeDesign, setActiveDesign] = useState<CatalogueDesignKey>(() => appearance?.design ?? firstDesign);
  const activeDesignLabel = useMemo(() => {
    const match = designOptions.find((option) => option.key === activeDesign);
    return match?.label ?? activeDesign;
  }, [designOptions, activeDesign]);


  useEffect(() => {
    if (appearance?.design && appearance.design !== activeDesign) {
      setActiveDesign(appearance.design);
    }
  }, [appearance?.design]);

  useEffect(() => {
    if (!designOptions.some((option) => option.key === activeDesign)) {
      setActiveDesign(firstDesign);
    }
  }, [designOptions, activeDesign, firstDesign]);

  const [remoteMeta, setRemoteMeta] = useState({
    etag: null as string | null,
    lastPublishedAt: null as string | null,
    isLoading: true,
    isSaving: false,
    error: null as Error | null,
  });

  const [publishedSnapshot, setPublishedSnapshot] = useState<CatalogueTemplateSnapshot | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRemoteMeta((prev) => ({ ...prev, isLoading: true, error: null }));
    fetchCatalogueTemplateSnapshot()
      .then((snapshot) => {
        if (cancelled) {
          return;
        }
        setPublishedSnapshot(snapshot);
        setRemoteMeta((prev) => ({
          ...prev,
          etag: snapshot.etag ?? null,
          lastPublishedAt: snapshot.updated_at,
          isLoading: false,
          error: null,
        }));
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setPublishedSnapshot(null);
        const normalized = error instanceof Error ? error : new Error(String(error));
        setRemoteMeta((prev) => ({ ...prev, isLoading: false, error: normalized }));
        message.warning('No se pudo obtener el diseño publicado actual.');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const draft = useTemplateDraft({
    template: templateParam,
    design: activeDesign,
    publishedOverrides: publishedSnapshot?.overrides ?? siteSettings?.catalogue_template_overrides ?? null,
  });

  const [editing, setEditing] = useState<{
    slotKey: string;
    entry: TemplateSlotEntry;
    originalKey: string;
  } | null>(null);

  const [activeTab, setActiveTab] = useState<LayoutEditorTabKey>('sections');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [isModalVisible, setModalVisible] = useState<boolean>(() => !draft.isMinimized);

  // Ref para acceder a los métodos de guardado de colores (debe estar antes de handlePersistChanges)
  const colorsPanelRef = useRef<ButtonPanelConfigEstiloRef>(null);
  const typographyPanelRef = useRef<TypographyPanelRef>(null);

  useEffect(() => {
    if (draft.isMinimized && editing) {
      setEditing(null);
    }
  }, [draft.isMinimized, editing]);

  // Track the _t param to detect menu re-clicks and auto-restore if minimized
  const menuClickTimestamp = params.get('_t');

  // Auto-restore when entering edit mode with panel minimized
  // This handles the case where user re-enters edit mode while panel was previously minimized
  useEffect(() => {
    if (draft.isMinimized) {
      draft.setDockMinimized(false);
      setModalVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuClickTimestamp]); // Run on mount AND when _t changes (menu re-click)

  useEffect(() => {
    if (draft.isMinimized) {
      setModalVisible(false);
    }
  }, [draft.isMinimized]);

  const isDockMinimized = draft.isMinimized;

  useEffect(() => {
    const { body } = document;
    if (!body) {
      return;
    }

    const shouldApplyPreviewMode = isModalVisible || isDockMinimized;

    if (!shouldApplyPreviewMode) {
      body.removeAttribute('data-catalogue-preview-mode');
      return;
    }

    body.setAttribute('data-catalogue-preview-mode', previewDevice);

    return () => {
      if (!isModalVisible && !isDockMinimized) {
        body.removeAttribute('data-catalogue-preview-mode');
      }
    };
  }, [previewDevice, isModalVisible, isDockMinimized]);

  useEffect(() => {
    setPreviewDevice('desktop');
  }, [activeDesign, templateParam]);

  const handleTemplateChange = (value: string) => {
    params.set('template', value);
    setParams(params, { replace: true });
  };

  const handleDesignChange = (value: CatalogueDesignKey) => {
    setActiveDesign(value);
    // Update appearance context to immediately reflect design change
    if (appearance) {
      appearance.setDesign(value);
    }
  };

  const handleItemAspectChange = useCallback((value: string) => {
    dispatch(setAspectRadio(value));
    draft.style.setCardAspectClass(value);
  }, [dispatch, draft.style]);

  // const openEditor = useCallback(
  //   (slotKey: string, entry: TemplateSlotEntry) => {
  //     setEditing({ slotKey, entry, originalKey: entry.key ?? '' });
  //   },
  //   [],
  // );

  const closeEditor = useCallback(() => setEditing(null), []);

  const handleModalSubmit = (nextEntry: TemplateSlotEntry) => {
    if (!editing) {
      return;
    }
    draft.updateEntry(editing.slotKey, editing.originalKey, nextEntry);
    setEditing(null);
  };

  const handlePersistChanges = useCallback(async () => {
    logInfo('handlePersistChanges called');
    const validation = draft.validateDraft();
    logInfo('Validation result:', validation);
    if (!validation.ok) {
      console.warn('Validation failed', validation);
      const details = validation.issues && validation.issues.length > 0 ? validation.issues[0] : validation.error?.message;
      message.error(details ?? 'El borrador contiene errores de esquema.');
      return;
    }

    setRemoteMeta((prev) => ({ ...prev, isSaving: true, error: null }));
    let templatePublished = false;
    try {
      // Refresh snapshot to get latest ETag before saving (prevents 412 mismatch)
      let currentEtag = remoteMeta.etag;
      try {
        const freshSnapshot = await fetchCatalogueTemplateSnapshot();
        currentEtag = freshSnapshot.etag ?? null;
        logInfo('[DEBUG] Refreshed ETag before save:', currentEtag);
      } catch (fetchError) {
        console.warn('[DEBUG] Could not refresh ETag, using cached:', currentEtag);
      }

      logInfo('Starting saveCatalogueTemplate...');
      logInfo('[DEBUG] saveCatalogueTemplate - etag:', currentEtag);
      logInfo('[DEBUG] saveCatalogueTemplate - overrides:', draft.draftEntry?.overrides);
      const submittedOverrides = draft.draftEntry?.overrides ?? null;
      const snapshot = await saveCatalogueTemplate(
        {
          design: activeDesign,
          template: templateParam,
          overrides: submittedOverrides,
        },
        { etag: currentEtag ?? undefined },
      );

      dispatch(
        mergeSiteSettingsLocal({
          catalogue_design: snapshot.design ?? activeDesign,
          catalogue_template_overrides: submittedOverrides,
        }),
      );

      setPublishedSnapshot(snapshot);
      setRemoteMeta((prev) => ({
        ...prev,
        etag: snapshot.etag ?? null,
        lastPublishedAt: snapshot.updated_at,
        isLoading: false,
        isSaving: false,
        error: null,
      }));

      draft.saveDraft(snapshot.updated_at ?? null);
      templatePublished = true;

      // Guardar colores también (flujo unificado)
      await colorsPanelRef.current?.saveColors({
        site_typography: typographyPanelRef.current?.getValue(),
      });
      typographyPanelRef.current?.markSaved();

      dispatch(
        mergeSiteSettingsLocal({
          catalogue_design: snapshot.design ?? activeDesign,
          catalogue_template_overrides: submittedOverrides,
        }),
      );

      message.success('Diseño, colores y tipografía guardados.');

      // Clear template definition cache FIRST to force reload with new configs
      resetTemplateDefinitionCache();

      // Refrescamos el snapshot completo para sincronizar el ETag y evitar conflictos 412 en guardados subsiguientes
      const latestSnapshot = await fetchCatalogueTemplateSnapshot();
      setPublishedSnapshot(latestSnapshot);
      setRemoteMeta((prev) => ({
        ...prev,
        etag: latestSnapshot.etag ?? null,
        lastPublishedAt: latestSnapshot.updated_at,
      }));

      // DEPRECATED: refreshAdminSettings causes React state desync - see docs/CODE_CLEANUP_REGISTRY.md
      // await refreshAdminSettings();
    } catch (error) {
      const normalized = error instanceof Error ? error : new Error(String(error));
      setRemoteMeta((prev) => ({ ...prev, error: normalized }));

      let detail = normalized.message;
      let shouldRefreshSnapshot = false;
      if (isAxiosError(error)) {
        const response = error.response;
        const responseDetail = (response?.data as { detail?: unknown })?.detail;
        if (typeof responseDetail === 'string') {
          detail = responseDetail;
        } else if (responseDetail && typeof (responseDetail as { detail?: string }).detail === 'string') {
          detail = (responseDetail as { detail?: string }).detail as string;
        }
        if (response?.status === 412) {
          shouldRefreshSnapshot = true;
        }
      }
      if (templatePublished) {
        message.warning('El diseño se publicó, pero los colores o la tipografía siguen pendientes.');
      } else {
        message.error(detail);
      }

      if (shouldRefreshSnapshot) {
        setRemoteMeta((prev) => ({ ...prev, isLoading: true }));
        try {
          const latestSnapshot = await fetchCatalogueTemplateSnapshot();
          setPublishedSnapshot(latestSnapshot);
          setRemoteMeta((prev) => ({
            ...prev,
            etag: latestSnapshot.etag ?? null,
            lastPublishedAt: latestSnapshot.updated_at,
            isLoading: false,
            error: null,
          }));
          message.warning('El diseño se actualizó en otra sesión. Se recargó la versión publicada.');
        } catch (refreshError) {
          const normalizedRefreshError =
            refreshError instanceof Error ? refreshError : new Error(String(refreshError));
          setRemoteMeta((prev) => ({ ...prev, isLoading: false, error: normalizedRefreshError }));
          message.warning('No se pudo sincronizar el diseño publicado más reciente.');
        }
      }
    } finally {
      setRemoteMeta((prev) => ({ ...prev, isSaving: false }));
    }
  }, [draft, activeDesign, templateParam, dispatch]);

  const handleMinimize = useCallback(() => {
    if (!draft.isMinimized) {
      draft.setDockMinimized(true);
      setModalVisible(false);
      message.info('Editor minimizado. Usá el dock flotante para reanudar.');
    }
  }, [draft]);

  const finalizeClose = useCallback(() => {
    draft.resetDraft();
    colorsPanelRef.current?.restoreColors();
    typographyPanelRef.current?.restore();
    setModalVisible(false);
    if (draft.isMinimized) {
      draft.setDockMinimized(false);
    }
    // Remove editTemplate, _t and catalogDesign parameters from URL when closing the editor
    const newParams = new URLSearchParams(params);
    newParams.delete('editTemplate');
    newParams.delete('_t');
    newParams.delete('catalogDesign');
    setParams(newParams, { replace: true });
  }, [draft, params, setParams]);

  // Track color changes
  const [hasUnsavedColors, setHasUnsavedColors] = useState(false);
  const [hasUnsavedTypography, setHasUnsavedTypography] = useState(false);
  // Check if design changed from published version
  const hasDesignChange = publishedSnapshot?.design !== activeDesign;
  const hasDraftChanges =
    Boolean(draft.draftEntry?.isDirty)
    || hasUnsavedColors
    || hasUnsavedTypography
    || hasDesignChange;

  const handleCloseModal = useCallback(() => {
    if (hasDraftChanges) {
      modal.confirm({
        title: 'Cerrar editor',
        content:
          'Tenés cambios sin publicar. Podés minimizarlos para retomarlos luego o cerrar ahora y mantenerlos locales en este navegador.',
        okText: 'Cerrar sin publicar',
        cancelText: 'Seguir editando',
        onOk: finalizeClose,
      });
      return;
    }
    finalizeClose();
  }, [hasDraftChanges, finalizeClose, modal]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !draft.isMinimized) {
        event.preventDefault();
        handleMinimize();
        return;
      }
      if ((event.metaKey || event.ctrlKey) && (event.key === 's' || event.key === 'S')) {
        event.preventDefault();
        void handlePersistChanges();
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [handleMinimize, draft.isMinimized, handlePersistChanges]);

  useEffect(() => {
    if (!draft.draftEntry?.isDirty && !hasUnsavedColors) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [draft.draftEntry?.isDirty, hasUnsavedColors]);

  // const slotEntries = draft.slots ? Object.entries(draft.slots) : [];
  // const hasSlots = slotEntries.length > 0;

  const savedAt = formatTimestamp(draft.draftEntry?.savedAt);
  const updatedAt = formatTimestamp(draft.draftEntry?.updatedAt);
  const publishedAt = formatTimestamp(remoteMeta.lastPublishedAt);

  const availableSections = draft.sections.available;

  const sectionsTabContent = (
    <div className="layout-editor-tab-panel">
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          {availableSections.length === 0 ? (
            <Alert
              type="info"
              showIcon
              title="El estilo actual no expone secciones configurables."
              description="Todas las secciones disponibles se muestran de forma predeterminada."
            />
          ) : (
            availableSections.map((sectionKey) => {
              const meta = sectionMetadata[sectionKey];
              const checked = draft.sections.visibility[sectionKey];
              return (
                <Checkbox
                  key={sectionKey}
                  checked={checked}
                  onChange={(event) => draft.sections.toggle(sectionKey, event.target.checked)}
                >
                  <Space orientation="vertical" size={4} style={{ display: 'flex' }}>
                    <Typography.Text strong>{meta.label}</Typography.Text>
                    {meta.description ? (
                      <Typography.Text type="secondary">{meta.description}</Typography.Text>
                    ) : null}
                  </Space>
                </Checkbox>
              );
            })
          )}
          {/* TODO: Filtros superiores - oculto hasta próxima iteración
          <Checkbox
            checked={draft.panel.topFiltersVisible}
            onChange={(event) => draft.panel.setTopFiltersVisible(event.target.checked)}
          >
            <Space orientation="vertical" size={4} style={{ display: 'flex' }}>
              <Typography.Text strong>{sectionMetadata.topFilters.label}</Typography.Text>
              <Typography.Text type="secondary">
                {sectionMetadata.topFilters.description}
              </Typography.Text>
            </Space>
          </Checkbox>
          */}
        </Space>

        <Divider style={{ margin: '8px 0' }} />

        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text strong>Posición de Banners Superiores</Typography.Text>
          <Typography.Text type="secondary">
            Define dónde se muestran los banners con ubicación "top".
          </Typography.Text>
          <Radio.Group
            value={draft.panel.topBannerPosition}
            onChange={(event) => draft.panel.setTopBannerPosition(event.target.value)}
            style={{ marginTop: 8 }}
          >
            <Space orientation="vertical" size={4}>
              <Radio value="grid">En la grilla de productos</Radio>
              <Radio value="hero">Debajo del encabezado de tienda</Radio>
            </Space>
          </Radio.Group>
        </Space>

        {/*<div className="layout-editor-tab-panel__section">*/}
        {/*  {draft.error ? (*/}
        {/*    <Alert*/}
        {/*      type="error"*/}
        {/*      showIcon*/}
        {/*      title="No se pudo cargar la definición declarativa"*/}
        {/*      description={draft.error.message}*/}
        {/*    />*/}
        {/*  ) : null}*/}

        {/*  <Spin spinning={draft.isLoading} tip="Cargando definición...">*/}
        {/*    <Space orientation="vertical" size={16} style={{ width: '100%' }}>*/}
        {/*      {!draft.isLoading && !hasSlots ? (*/}
        {/*        <Empty description="Este diseño todavía no cuenta con una definición declarativa." />*/}
        {/*      ) : null}*/}

        {/*      {slotEntries.map(([slotKey, slotList]) => (*/}
        {/*        <SlotListEditor*/}
        {/*          key={slotKey}*/}
        {/*          slotKey={slotKey}*/}
        {/*          title={slotKey.toUpperCase()}*/}
        {/*          description={slotDescriptions[slotKey]}*/}
        {/*          entries={slotList}*/}
        {/*          availableBlocks={getBlockOptionsForSlot(slotKey, draft.availableBlocks)}*/}
        {/*          onAddBlock={(block) => draft.addBlock(slotKey, block)}*/}
        {/*          onRemoveBlock={(entryKey) => draft.removeBlock(slotKey, entryKey)}*/}
        {/*          onMoveBlock={(fromIndex, toIndex) => draft.moveBlock(slotKey, fromIndex, toIndex)}*/}
        {/*          onEditBlock={(entry) => openEditor(slotKey, entry)}*/}
        {/*        />*/}
        {/*      ))}*/}
        {/*    </Space>*/}
        {/*  </Spin>*/}
        {/*</div>*/}
      </Space>
    </div >
  );

  // Prioritize the aspect ratio from the draft (template override) over the global config
  const effectiveItemAspectRatio = draft.style.cardAspectClass ?? globalItemAspectRatio;
  const effectiveItemCardSize = draft.style.itemCardSize ?? siteSettings?.catalogue_item_card_size ?? 'normal';

  const styleTabContent = (
    <div className="layout-editor-tab-panel">
      <LayoutEditorModal.StylePanel
        typographyPanel={(
          <TypographyPanel
            ref={typographyPanelRef}
            onDirtyChange={setHasUnsavedTypography}
          />
        )}
        bannerAspectClass={draft.style.bannerAspectClass}
        bannerAspectDefaultRatio={draft.style.bannerAspectDefaultRatio}
        onBannerAspectChange={draft.style.setBannerAspectClass}
        itemAspectRatio={effectiveItemAspectRatio}
        onItemAspectChange={handleItemAspectChange}
        itemCardSize={effectiveItemCardSize}
        onItemCardSizeChange={draft.style.setItemCardSize}
      />
    </div>
  );

  const colorsTabContent = (
    <div className="layout-editor-tab-panel">
      <LayoutEditorModal.ColorsPanel
        onAfterSave={() => {
          // DEPRECATED: refreshAdminSettings causes React state desync - see docs/CODE_CLEANUP_REGISTRY.md
          // refreshAdminSettings();
          setHasUnsavedColors(false);
        }}
        colorsPanelRef={colorsPanelRef}
        onDirtyChange={setHasUnsavedColors}
      />
    </div>
  );

  const viewTabContent = (
    <div className="layout-editor-tab-panel">
      <LayoutEditorModal.ViewPanel
        device={previewDevice}
        onDeviceChange={setPreviewDevice}
        activeDesignLabel={activeDesignLabel}
      />
    </div>
  );

  return (
    <>
      {modalContextHolder}
      <LayoutEditorModal
        open={isModalVisible && !draft.isMinimized}
        onClose={handleCloseModal}
        onMinimize={handleMinimize}
        onTabChange={setActiveTab}
        activeTab={activeTab}
        templateValue={templateParam}
        templateOptions={templateOptions}
        onTemplateChange={handleTemplateChange}
        designValue={activeDesign}
        designOptions={designOptions}
        onDesignChange={handleDesignChange}
        isSaving={remoteMeta.isSaving}
        isLoading={remoteMeta.isLoading}
        hasDraftChanges={hasDraftChanges}
        savedAt={savedAt}
        updatedAt={updatedAt}
        publishedAt={publishedAt}
        onPublish={handlePersistChanges}
        sectionsTab={sectionsTabContent}
        styleTab={styleTabContent}
        colorsTab={colorsTabContent}
        viewTab={viewTabContent}
        allowPublish={!remoteMeta.isLoading}
      />

      <BlockSettingsModal
        open={Boolean(editing)}
        entry={editing?.entry ?? null}
        onSubmit={handleModalSubmit}
        onCancel={closeEditor}
      />
    </>
  );
};

export default TemplateConfigurator;
